<script>
import CheckInAction from "@/views/trips/view/actions/CheckInAction.vue";
import GoodToGoAction from "@/views/trips/view/actions/GoodToGoAction.vue";
import LoadVerificationAction from "@/views/trips/view/actions/LoadVerificationAction.vue";
import UnloadVerificationAction from "@/views/trips/view/actions/UnloadVerificationAction.vue";
import { RouteStatus, RouteStopStatus } from "@/data/trip";
import { OrderStopState, OrderStopType } from "@/data/order";
import RouteStopStatusLabel from "@/views/trips/components/RouteStopStatus.vue";
import StepperItem from "@/views/trips/components/StepperItem.vue";
import DistanceLine from "@/views/trips/components/DistanceLine.vue";
import StopTimer from "@/views/trips/components/StopTimer.vue";
import RouteStopFreightDetails from "@/views/trips/components/RouteStopFreightDetails.vue";
import RouteStopActions from "@/views/trips/components/RouteStopActions.vue";
import LoadAction from "@/views/trips/view/actions/LoadAction.vue";
import UnloadAction from "@/views/trips/view/actions/UnloadAction.vue";
import ShowInfoAction from "@/views/trips/view/actions/ShowInfoAction.vue";
import FinishAction from "@/views/trips/view/actions/FinishAction.vue";
import StopChangesPopover from "@/views/trips/components/StopChangesPopover.vue";
import FacilityInfo from "@/views/trips/components/FacilityInfo.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import OrderActionsTime from "@/components/order/OrderActionsTime.vue";
import RouteStopIcon from "@/components/routeStop/RouteStopIcon.vue";
import { useTrip } from "@/composables/index.js";
import PermissionTooltip from "@/components/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto.ts";

const { isTravelOrderCancelled } = useTrip();

export default {
  name: "RouteStop",
  components: {
    PermissionTooltip,
    RouteStopIcon,
    OrderActionsTime,
    AlertMessage,
    FacilityInfo,
    StopChangesPopover,
    FinishAction,
    ShowInfoAction,
    UnloadAction,
    LoadAction,
    RouteStopActions,
    RouteStopFreightDetails,
    StopTimer,
    DistanceLine,
    StepperItem,
    RouteStopStatusLabel,
    UnloadVerificationAction,
    LoadVerificationAction,
    GoodToGoAction,
    CheckInAction,
  },
  props: {
    stop: Object,
    route: Object,
    trip: Object,
    index: Number,
    undoable: Boolean,
  },
  emits: ["change"],
  computed: {
    DispatcherPermissionValue() {
      return DispatcherPermissionValue;
    },
    isEmpty() {
      return !this.stop.id;
    },
    isReload() {
      return this.stop.isReload;
    },
    isNew() {
      return this.stop.status === RouteStopStatus.notStarted;
    },
    isReadyForCheckIn() {
      return this.route.dispatchConfirmed && this.stop.status === RouteStopStatus.inRoute;
    },
    isReadyForLoading() {
      return this.stop.status === RouteStopStatus.waitingForLoading && this.isPickUp;
    },
    isReadyForLoadingVerification() {
      return this.stop.status === RouteStopStatus.waitingForVerification && this.isPickUp;
    },
    isReadyForUnloading() {
      return this.stop.status === RouteStopStatus.waitingForLoading && this.isDelivery;
    },
    isReadyForUnloadingVerification() {
      return this.stop.status === RouteStopStatus.waitingForVerification && this.isDelivery;
    },
    isReadyForGoodToGo() {
      return this.stop.status === RouteStopStatus.waitingForGoodToGo;
    },
    isComplete() {
      return this.stop.status === RouteStopStatus.completed;
    },
    isActive() {
      return !this.isNew && !this.isComplete;
    },
    isCheckedIn() {
      return this.stop.checkInTime;
    },
    isPickUp() {
      return this.stop.type === OrderStopType.pickup;
    },
    isDelivery() {
      return this.stop.type === OrderStopType.delivery;
    },
    stopType() {
      switch (this.stop.type) {
        case OrderStopType.pickup:
          return "Pick up";
        case OrderStopType.delivery:
          return "Delivery";
        default:
          return "";
      }
    },
    freights() {
      return this.route.freights;
    },
    stopFreights() {
      return this.stop.freights.map((sf) => this.freights.find((f) => f.id === sf)).filter((f) => f !== undefined);
    },
    quantity() {
      return this.stopFreights.reduce((sum, f) => sum + f.plannedQuantity, 0);
    },
    weight() {
      return this.stopFreights.reduce((sum, f) => sum + f.plannedWeight, 0);
    },
    distance() {
      const nextStop = this.route.routeStops[this.index - 1];
      return nextStop ? this.route.distances[this.stop.id + "-" + nextStop.id] : null;
    },
    isCanceled() {
      const relatedTravelOrders = this.route.travelOrders.filter((to) => this.stop.travelOrderIds.includes(to.id));

      return relatedTravelOrders.length && relatedTravelOrders.every((to) => isTravelOrderCancelled(to));
    },
    isUpdated() {
      return !this.isDeleted && this.stop.state === OrderStopState.updated;
    },
    isDeleted() {
      return this.stop.isDeleted;
    },
    isRedispatchNotConfirmed() {
      return this.route.redispatch === "not-confirmed";
    },
    isPlanned() {
      return this.route.status === RouteStatus.planned;
    },
    isRouteCancelled() {
      return this.route.status === RouteStatus.cancelled;
    },
    stopClass() {
      if (this.isEmpty) {
        return "bg-white border border-grey-lighten-1";
      } else if (this.isCanceled) {
        return "bg-red-lighten-5";
      } else if (this.isPlanned) {
        return "bg-indigo-lighten-5";
      } else {
        return "bg-grey-lighten-4";
      }
    },
    isLastStop() {
      const activeRouteStops = this.route.routeStops.filter((routeStop) => routeStop.id && !routeStop.isDeleted);
      return activeRouteStops.findIndex((routeStop) => routeStop.id === this.stop.id) === activeRouteStops.length - 1;
    },
    travelOrders() {
      return this.route.travelOrders.filter((travelOrder) => {
        return this.stop.travelOrderIds.includes(travelOrder.id);
      });
    },
  },
  methods: {
    onChange() {
      this.$emit("change");
    },
  },
};
</script>

<template>
  <stepper-item
    :active="isActive"
    :complete="isComplete"
    :is-cancelled="isRouteCancelled"
    :is-deleted="isDeleted"
    :last="isLastStop"
    :number="index"
  >
    <div :class="stopClass" class="rounded pa-4 my-2 route-stop d-flex flex-column" data-qa="route-stop">
      <template v-if="isEmpty">
        <div class="d-flex align-center justify-center flex-column flex-1">
          <v-icon class="mb-3" color="grey" size="34">mdi-alert-octagon-outline</v-icon>
          <span class="font-size-16 text-grey">Not used in this route</span>
        </div>
      </template>

      <div v-else>
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <route-stop-icon
              :is-deleted="stop.isDeleted"
              :is-reload="stop.isReload"
              :type="stop.type"
              class="mr-2"
              size="20"
            ></route-stop-icon>

            <span v-if="isReload" class="font-size-12 text-blue-grey-darken-1 mr-1">Reload stop - </span>
            <div
              :class="isDeleted ? 'text-grey' : 'text-blue-grey-darken-1'"
              class="font-size-12 font-weight-600"
              data-qa="route-stops-stop-type"
            >
              {{ stopType }} #{{ stop.number }}
            </div>
            <router-link
              v-if="!isDeleted && stop.order"
              :to="{ name: 'order-view', params: { id: stop.order.id } }"
              class="d-block text-primary font-weight-500 font-size-12 text-decoration-none ml-3"
              data-qa="route-stops-order-number"
              target="_blank"
            >
              Order #{{ stop.order.number }}
            </router-link>
            <span v-if="isDeleted" class="text-grey font-weight-500 font-size-12 ml-3">
              Order #{{ stop.order.number }}
            </span>
            <stop-changes-popover v-if="isUpdated" :freights="stopFreights" :stop="stop"></stop-changes-popover>
          </div>
          <div class="d-flex align-center">
            <stop-timer
              v-if="isCheckedIn"
              :end-time="stop.checkOutTime"
              :start-time="stop.checkInTime"
              class="mr-4"
            ></stop-timer>
            <route-stop-actions
              v-if="(undoable || isComplete) && !isCanceled"
              :route="route"
              :stop="stop"
              :trip="trip"
              class="ml-3"
              @change="onChange"
            ></route-stop-actions>
          </div>
          <div v-if="isUpdated" class="badge bg-warning pa-2 rounded-te rounded-bs font-weight-600">UPDATED</div>
          <div v-if="isDeleted" class="badge bg-grey-darken-1 pa-2 rounded-te rounded-bs font-weight-600">DELETED</div>
        </div>
        <route-stop-status-label
          v-if="!isDeleted"
          :is-cancelled="isCanceled"
          :stop="stop"
          class="mt-2"
        ></route-stop-status-label>
        <alert-message
          v-if="isDeleted"
          bg-color="indigo-lighten-5"
          border-color="none"
          class="mt-3"
          color="indigo"
          icon="mdi-information-outline"
          icon-color="indigo"
        >
          Stop will be removed from trip after re-dispatch confirmation
        </alert-message>
        <facility-info
          v-if="stop.facility"
          :disabled="isDeleted"
          :facility="stop.facility"
          :updated="false"
          class="mt-4"
        ></facility-info>
        <div :class="isDeleted ? 'text-grey' : 'text-grey-darken-1'" class="d-flex font-weight-500 mt-5">
          <div class="mr-2">
            <v-icon icon="mdi-clock-outline" size="20"></v-icon>
          </div>
          <order-actions-time
            :check-in-time="stop.checkInTime"
            :check-out-time="stop.checkOutTime"
            :load-unload-time="stop.loadUnloadTime"
            :stop-type="stop.type"
            :time-to="stop.timeTo"
            :timezone="stop.timezone"
            style="margin-top: 0.1rem"
          />
        </div>
        <route-stop-freight-details
          :disabled="isDeleted"
          :freights="stopFreights"
          :stop="stop"
        ></route-stop-freight-details>

        <template v-if="!isCanceled && !isRedispatchNotConfirmed">
          <permission-tooltip :permission="DispatcherPermissionValue.ProcessTrip">
            <template #activator="{ disabled }">
              <check-in-action
                v-if="isReadyForCheckIn"
                :disabled="disabled"
                :route="route"
                :stop="stop"
                :trip="trip"
                class="mt-6 text-uppercase font-weight-600"
                @checked-in="onChange"
              ></check-in-action>
              <load-action
                v-if="isReadyForLoading"
                :disabled="disabled"
                :route="route"
                :stop="stop"
                :trip="trip"
                class="mt-6 text-uppercase font-weight-600"
                color="primary"
                @load="onChange"
              ></load-action>
              <load-verification-action
                v-if="isReadyForLoadingVerification"
                :disabled="disabled"
                :route="route"
                :stop="stop"
                :trip="trip"
                class="mt-6"
                @verify="onChange"
              ></load-verification-action>
              <unload-action
                v-if="isReadyForUnloading"
                :disabled="disabled"
                :route="route"
                :stop="stop"
                :trip="trip"
                class="mt-6"
                @unload="onChange"
              ></unload-action>
              <unload-verification-action
                v-if="isReadyForUnloadingVerification"
                :disabled="disabled"
                :route="route"
                :stop="stop"
                :trip="trip"
                class="mt-6"
                @verify="onChange"
              ></unload-verification-action>
              <v-row v-if="isReadyForGoodToGo" class="mt-6">
                <v-col>
                  <show-info-action :disabled="disabled" :route="route" :stop="stop" :trip="trip"></show-info-action>
                </v-col>
                <v-col>
                  <good-to-go-action
                    v-if="!isLastStop"
                    :disabled="disabled"
                    :route="route"
                    :stop="stop"
                    :trip="trip"
                    @finish="onChange"
                  ></good-to-go-action>
                  <finish-action
                    v-else
                    :disabled="disabled"
                    :route="route"
                    :stop="stop"
                    :trip="trip"
                    @finish="onChange"
                  ></finish-action>
                </v-col>
              </v-row>
            </template>

            {{ $t("permission.process-trip") }}
          </permission-tooltip>
        </template>
      </div>
    </div>
    <distance-line v-if="distance" :value="distance"></distance-line>
  </stepper-item>
</template>

<style lang="scss" scoped>
.route-stop {
  min-height: 270px;
}

.badge {
  margin-top: -16px;
  margin-right: -16px;
}
</style>
